/**
 * PanelToggle — a two-position switch in the card's language.
 *
 * The same sliding-pill mechanic as SegmentedToggle: one absolutely-positioned element moved with
 * `translateX`, not a background on the active half. A transform animates on the compositor, and it
 * is what makes the control read as one switch rather than two buttons.
 *
 * DISABLED READS AS LOCKED, NOT AS OFF, and the distinction is the whole reason this prop exists.
 * The node editor greys a toggle whose parent is off — "Interiors" when a tower has no buildings —
 * and if that looked identical to "off" the user would tap it and wonder why nothing happened. So a
 * locked switch keeps its own position and loses only its contrast, and the caller passes `title`
 * to say why.
 */

import { CONTROL_H, TRACK, ACCENT_BG, ACCENT_ON, SEGMENT_OFF, SEGMENT_DISABLED, MOTION } from './tokens';

interface PanelToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  /** Shown inside the two halves. Defaults to Off / On. */
  labels?: readonly [string, string];
  disabled?: boolean;
  /** Why it is locked — surfaced as a tooltip, like the old dialog's pills. */
  title?: string;
  ariaLabel?: string;
}

export function PanelToggle({
  value, onChange, labels = ['Off', 'On'], disabled, title, ariaLabel,
}: PanelToggleProps) {
  return (
    <div
      role="switch"
      aria-checked={value}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      title={title}
      className={`relative ${CONTROL_H} ${TRACK} p-1 ${disabled ? 'opacity-50' : ''}`}
    >
      <div className="absolute inset-1 pointer-events-none">
        <div
          className={`h-full w-1/2 rounded-full ${ACCENT_BG} shadow-sm ${MOTION}`}
          style={{ transform: `translateX(${value ? 100 : 0}%)`, opacity: value ? 1 : 0.28 }}
        />
      </div>
      <div className="relative h-full flex">
        {labels.map((label, i) => (
          <button
            key={label}
            type="button"
            disabled={disabled}
            onClick={() => onChange(i === 1)}
            className={`flex-1 min-w-0 h-full rounded-full text-xs md:text-[11px] font-bold ${MOTION} ${
              (i === 1) === value
                ? (disabled ? SEGMENT_DISABLED : ACCENT_ON)
                : disabled ? SEGMENT_DISABLED : `${SEGMENT_OFF} cursor-pointer hover:text-neutral-800`
            }`}
          >
            <span className="block truncate px-1">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
